import { ChainData } from "@0xsquid/squid-types";
import { useMemo } from "react";
import { chainTypeToNativeTokenAddressMap, Token } from "../../core";
import {
  ChainFeeParams,
  convertTempoFeeToStablecoinUnits,
  isTempoChain,
} from "../../services/internal/tempoService";
import { useTempoFeeCheck } from "../transaction/useTempoFeeCheck";
import { useNativeBalance } from "./useNativeBalance";
import { useNativeTokenForChain } from "./useNativeTokenForChain";
import { useSquidTokens } from "./useSquidTokens";

/**
 * Hook that returns the gas token and chain fee params for a given source chain and token.
 */
export function useSourceChainGasToken({
  fromChain,
  fromToken,
}: {
  fromChain: ChainData | undefined;
  fromToken: Token | undefined;
}): { gasToken: Token | undefined; chainFeeParams: ChainFeeParams | null } {
  const { nativeToken } = useNativeTokenForChain(fromChain);
  const { evmTokens } = useSquidTokens();
  const { nativeBalance } = useNativeBalance(fromChain);
  const tempoFeeData = useTempoFeeCheck({ fromChain, fromToken });

  const chainFeeParams = useMemo((): ChainFeeParams | null => {
    if (
      fromToken?.address == null ||
      fromChain?.chainId == null ||
      nativeBalance?.value == null
    ) {
      return null;
    }

    if (isTempoChain(fromChain.chainId)) {
      if (!tempoFeeData) return null;

      const fromTokenPaysGasFees =
        tempoFeeData.gasTokenAddress.toLowerCase() ===
        fromToken.address.toLowerCase();
      return {
        fromTokenPaysGasFees,
        gasTokenBalanceWei: tempoFeeData.gasBalance,
        normalizeFee: convertTempoFeeToStablecoinUnits,
      };
    }

    return {
      fromTokenPaysGasFees:
        fromToken.address.toLowerCase() ===
        chainTypeToNativeTokenAddressMap[fromToken.type].toLowerCase(),
      gasTokenBalanceWei: nativeBalance.value,
      normalizeFee: (fee) => fee,
    };
  }, [
    fromToken?.address,
    fromToken?.type,
    fromChain?.chainId,
    nativeBalance?.value,
    tempoFeeData,
  ]);

  /**
   * The token to pay for gas on the source chain.
   */
  const gasToken = useMemo(() => {
    if (fromChain?.chainId == null || fromToken?.address == null)
      return undefined;

    if (isTempoChain(fromChain.chainId)) {
      if (!tempoFeeData) return undefined;

      return evmTokens.find(
        (t) =>
          t.chainId === fromChain.chainId &&
          t.address.toLowerCase() === tempoFeeData.gasTokenAddress.toLowerCase()
      );
    }

    return nativeToken;
  }, [
    fromChain?.chainId,
    fromToken?.address,
    nativeToken,
    tempoFeeData,
    evmTokens,
  ]);

  return { gasToken, chainFeeParams };
}
